import { IconProp } from "@fortawesome/fontawesome-svg-core"

import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { useReportDashboardsQuery } from "@pages/Reports/Reports.store"

import { BILLS_ROUTES } from "@routes/bills"
import { CALENDAR_ROUTE } from "@routes/calendar"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { HOME_ROUTE } from "@routes/home"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { JOB_SERIES_ROUTES } from "@routes/jobs-series"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { REPORTS_ROUTE } from "@routes/reports"
import { VENDOR_ROUTES } from "@routes/vendor"
import { VENDOR_CREDITS_ROUTES } from "@routes/vendorCredits"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import useHasAccess from "./useHasAccess"
import useSlugExtractor from "./useSlugExtractor"

export type NavigationLink = {
    label: string
    path: string
    subLinks?: NavigationLink[]
    predicate?: (user: User) => boolean
    icon?: IconProp
    iconActive?: IconProp
}

export default function useNavigationLinks() {
    const { user } = useUser()

    const { hasEntitlement, hasPermission, hasFlag } = useHasAccess()
    const { data: reportDashboards = [] } = useReportDashboardsQuery(user)

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [
        homeRoute,
        calendarRoute,
        clientsListRoute,
        estimatesListRoute,
        jobsListRoute,
        jobSeriesListRoute,
        invoicesListRoute,
        creditNotesListRoute,
        purchaseOrdersListRoute,
        billsListRoute,
        vendorCreditsListRoute,
        vendorsListRoute,
        pricebookServicesListRoute,
        pricebookPartsOrMaterialsListRoute,
        pricebookMiscellaneousListRoute,
        pricebookDiscountsListRoute,
        pricebookTaxRatesListRoute,
        reportsRoute,
    ] = useSlugExtractor([
        HOME_ROUTE,
        CALENDAR_ROUTE,
        CLIENTS_ROUTES.LIST,
        ESTIMATES_ROUTES.LIST,
        JOBS_ROUTES.LIST,
        JOB_SERIES_ROUTES.LIST,
        INVOICES_ROUTES.LIST,
        CREDIT_NOTES_ROUTES.LIST,
        PURCHASE_ORDER_ROUTES.LIST,
        BILLS_ROUTES.LIST,
        VENDOR_CREDITS_ROUTES.LIST,
        VENDOR_ROUTES.LIST,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.LIST,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.LIST,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.LIST,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.LIST,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.LIST,
        REPORTS_ROUTE,
    ])

    const home: NavigationLink = {
        path: homeRoute,
        label: "Home",
        icon: faIcon({ name: "house", style: "regular" }),
        iconActive: faIcon({ name: "house", style: "solid" }),
    }

    const calendar: NavigationLink = {
        path: calendarRoute,
        label: "Calendar",
        predicate: (user: User) =>
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_list_permission", PERMISSION_LEVEL.FULL) &&
            user.service_company.feature_legacy_calendar_enabled,
        icon: faIcon({ name: "objects-align-left", style: "regular" }),
        iconActive: faIcon({ name: "objects-align-left", style: "solid" }),
    }

    const clients: NavigationLink = {
        path: clientsListRoute,
        label: objectMap.client.labelPlural,
        predicate: () => hasPermission("clients_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.client.icon,
        iconActive: objectMap.client.iconActive,
    }

    const estimates: NavigationLink = {
        path: estimatesListRoute,
        label: objectMap.estimate.labelPlural,
        predicate: () =>
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.estimate.icon,
        iconActive: objectMap.estimate.iconActive,
    }

    const jobs: NavigationLink = {
        path: "",
        label: objectMap.job.labelPlural,
        predicate: () =>
            hasEntitlement("entitlement_jobs_enabled") && hasPermission("jobs_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.job.icon,
        iconActive: objectMap.job.iconActive,
        subLinks: [
            {
                label: objectMap.job.labelPlural,
                path: jobsListRoute,
            },
            {
                label: objectMap.job_series.labelPlural,
                path: jobSeriesListRoute,
                predicate: () => !hasFlag("restricted_to_assigned_jobs"),
            },
        ],
    }

    const invoices: NavigationLink = {
        path: "",
        label: objectMap.invoice.labelPlural,
        predicate: () =>
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.invoice.icon,
        iconActive: objectMap.invoice.iconActive,
        subLinks: [
            {
                label: objectMap.invoice.labelPlural,
                path: invoicesListRoute,
            },
            {
                label: objectMap.credit_note.labelPlural,
                path: creditNotesListRoute,
                predicate: (user: User) => !!user.service_company.feature_credit_notes_enabled,
            },
        ],
    }

    const purchase_orders: NavigationLink = {
        path: purchaseOrdersListRoute,
        label: objectMap.purchase_order.labelPlural,
        predicate: () =>
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.purchase_order.icon,
        iconActive: objectMap.purchase_order.iconActive,
    }

    const bills: NavigationLink = {
        path: "",
        label: objectMap.bill.labelPlural,
        predicate: () =>
            hasEntitlement("entitlement_bills_enabled") &&
            hasPermission("bills_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.bill.icon,
        iconActive: objectMap.bill.iconActive,
        subLinks: [
            {
                label: objectMap.bill.labelPlural,
                path: billsListRoute,
            },
            {
                label: objectMap.vendor_credit.labelPlural,
                path: vendorCreditsListRoute,
                predicate: (user: User) => !!user.service_company.feature_vendor_credits_enabled,
            },
        ],
    }

    const vendors: NavigationLink = {
        path: vendorsListRoute,
        label: objectMap.vendor.labelPlural,
        predicate: () => hasPermission("vendors_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.vendor.icon,
        iconActive: objectMap.vendor.iconActive,
    }

    const pricebook: NavigationLink = {
        path: "",
        label: objectMap.pricebook_item.labelPlural,
        predicate: () => hasPermission("pricebook_list_permission", PERMISSION_LEVEL.FULL),
        icon: objectMap.pricebook_item.icon,
        iconActive: objectMap.pricebook_item.iconActive,
        subLinks: [
            {
                label: "Services",
                path: pricebookServicesListRoute,
            },
            {
                label: "Parts & Materials",
                path: pricebookPartsOrMaterialsListRoute,
            },
            {
                label: "Miscellaneous",
                path: pricebookMiscellaneousListRoute,
            },
            {
                label: "Discounts",
                path: pricebookDiscountsListRoute,
            },
            ...(isUsingTaxRates
                ? [
                      {
                          label: "Tax Rates",
                          path: pricebookTaxRatesListRoute,
                      },
                  ]
                : []),
        ],
    }

    const reportSubLinks =
        reportDashboards.length > 0
            ? reportDashboards.map((dashboard) => ({
                  label: dashboard.name,
                  path: `${reportsRoute}?report=${dashboard.id}`,
              }))
            : undefined

    const reports: NavigationLink = {
        path: reportDashboards.length === 0 ? reportsRoute : "",
        label: "Reports",
        predicate: (user: User) =>
            user.service_company.feature_reports_enabled &&
            hasPermission("reports_view_permission", PERMISSION_LEVEL.FULL),
        icon: faIcon({ name: "chart-mixed", style: "regular" }),
        iconActive: faIcon({ name: "chart-mixed", style: "solid" }),
        subLinks: reportSubLinks,
    }

    return {
        home,
        calendar,
        clients,
        estimates,
        jobs,
        invoices,
        purchase_orders,
        bills,
        vendors,
        pricebook,
        reports,
    }
}
